<template>
  <nav class="catalog-rail" data-qa="accessory-catalog-rail">
    <div class="catalog-rail__group">
      <button
        v-for="filter in quickFilters"
        :key="filter.key"
        :class="['catalog-rail__item', { 'is-on': isQuickSelected(filter.key) }]"
        type="button"
        :aria-current="isQuickSelected(filter.key) ? 'true' : null"
        :data-qa="`catalog-filter-${filter.key}`"
        @click="$emit('select', { kind: 'quick', quick: filter.key, path: [] })"
      >
        <span class="lbl">{{ filter.label }}</span>
        <span class="cnt">{{ counts[filter.key] ?? 0 }}</span>
      </button>
    </div>

    <div class="catalog-rail__group">
      <div class="catalog-rail__label">{{ i18n.t('/AccessoryCatalog/CategoriesLabel') }}</div>
      <template v-for="node in tree" :key="pathKey(node)">
        <button
          :class="[
            'catalog-rail__item',
            { 'is-on': isPathSelected(node.path), open: isOpen(node) },
          ]"
          type="button"
          :aria-current="isPathSelected(node.path) ? 'true' : null"
          :aria-expanded="hasToggle(node) ? String(isOpen(node)) : null"
          @click="selectNode(node)"
        >
          <icon v-if="hasToggle(node)" aria-hidden="true" icon="chevron-right" class="chev" />
          <span v-else class="chev-spacer" />
          <span class="lbl">{{ categoryLabel(node.name) }}</span>
          <span class="cnt">{{ node.count }}</span>
        </button>
        <div v-if="isOpen(node)" class="catalog-rail__sub">
          <template v-for="child in node.children" :key="pathKey(child)">
            <button
              :class="[
                'catalog-rail__item',
                { 'is-on': isPathSelected(child.path), open: isOpen(child) },
              ]"
              type="button"
              :aria-current="isPathSelected(child.path) ? 'true' : null"
              :aria-expanded="hasToggle(child) ? String(isOpen(child)) : null"
              @click="selectNode(child)"
            >
              <icon v-if="hasToggle(child)" aria-hidden="true" icon="chevron-right" class="chev" />
              <span v-else class="chev-spacer" />
              <span class="lbl">{{ categoryLabel(child.name) }}</span>
              <span class="cnt">{{ child.count }}</span>
            </button>
            <div v-if="isOpen(child)" class="catalog-rail__sub2">
              <button
                v-for="leaf in child.children"
                :key="pathKey(leaf)"
                :class="['catalog-rail__item', { 'is-on': isPathSelected(leaf.path) }]"
                type="button"
                :aria-current="isPathSelected(leaf.path) ? 'true' : null"
                @click="$emit('select', { kind: 'category', path: leaf.path })"
              >
                <span class="lbl">{{ categoryLabel(leaf.name) }}</span>
                <span class="cnt">{{ leaf.count }}</span>
              </button>
            </div>
          </template>
        </div>
      </template>
    </div>

    <div class="catalog-rail__source">
      <b>{{ i18n.t('/AccessoryCatalog/SourceNoteTitle') }}</b>
      {{ i18n.t('/AccessoryCatalog/SourceNote') }}
    </div>
  </nav>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import { categoryLabel } from '@/helpers/ponCatalogFilters'

export default {
  components: {
    Icon,
  },
  props: {
    /** Tree from buildCategoryTree */
    tree: {
      type: Array,
      default: () => [],
    },
    /** Counts per quick filter key from quickFilterCounts */
    counts: {
      type: Object,
      default: () => ({}),
    },
    /** Current selection: { kind: 'quick'|'category', quick, path } */
    selection: {
      type: Object,
      required: true,
    },
  },
  emits: ['select'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      openPaths: [],
    }
  },
  computed: {
    quickFilters() {
      return [
        { key: 'all', label: this.i18n.t('/AccessoryCatalog/FilterAll') },
        { key: 'action', label: this.i18n.t('/AccessoryCatalog/FilterAction') },
        { key: 'inStock', label: this.i18n.t('/AccessoryCatalog/FilterInStock') },
        { key: 'onQuote', label: this.i18n.t('/AccessoryCatalog/FilterOnQuote') },
      ]
    },
  },
  watch: {
    /**
     * Searching rebuilds the tree, and the open state is per path key, so it
     * outlived the nodes it belonged to: collapsing a branch and then clearing
     * the search reopened it with its grandchildren still expanded. Resetting to
     * the branch of the current selection makes the open state a pure function
     * of the selection whenever the tree changes — the user sees where they are
     * and nothing else.
     */
    tree: {
      immediate: true,
      handler() {
        this.openPaths = this.selectionBranch()
      },
    },
  },
  methods: {
    categoryLabel,
    isQuickSelected(key) {
      return this.selection.kind === 'quick' && this.selection.quick === key
    },
    isPathSelected(path) {
      return this.selection.kind === 'category' && this.selection.path.join('|') === path.join('|')
    },
    pathKey(node) {
      return node.path.join('|')
    },
    isOpen(node) {
      return this.openPaths.includes(this.pathKey(node))
    },
    /** The path keys of the selected node and every ancestor above it. */
    selectionBranch() {
      if (this.selection.kind !== 'category') return []
      return this.selection.path.map((name, index) =>
        this.selection.path.slice(0, index + 1).join('|')
      )
    },
    /**
     * Whether the node shows a chevron and toggles on click. Below the top level
     * a single leaf that repeats its parent's name adds no information, so it
     * does not earn a toggle. Drives both the chevron and `selectNode`, which
     * used to re-derive the same rule.
     */
    hasToggle(node) {
      if (node.path.length === 1) return node.children.length > 0
      return (
        node.children.length > 1 ||
        (node.children.length === 1 && node.children[0].name !== node.name)
      )
    },
    selectNode(node) {
      this.$emit('select', { kind: 'category', path: node.path })
      if (!this.hasToggle(node)) return
      const key = this.pathKey(node)
      this.openPaths = this.openPaths.includes(key)
        ? this.openPaths.filter((p) => p !== key)
        : this.openPaths.concat(key)
    },
  },
}
</script>

<style lang="scss" scoped>
// Foreground on a --color-primary-light background. --color-primary is a brand
// accent meant for fills and borders: on its own light tint it drops to 1.9:1
// (SEAT) and 3.7:1 (CUPRA), both under the 4.5:1 AA floor. $text-color clears
// 9.3:1 on the darkest tint any of the six brands ships.
$on-primary-light: $text-color;

.catalog-rail {
  background: $white;
  overflow-y: auto;
  padding: 14px 0 30px;

  &__group {
    padding: 0 12px 10px;
  }

  &__label {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: $gray-dark;
    padding: 10px 10px 6px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    border: 0;
    background: none;
    text-align: left;
    padding: 8px 10px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 14px;
    color: $text-color;
    cursor: pointer;
    transition:
      background 0.12s ease,
      color 0.12s ease;

    &:hover {
      background: $gray-lightest;
    }

    &.is-on {
      background: var(--color-primary-light);
      color: $on-primary-light;
      font-weight: 700;

      .cnt {
        color: $on-primary-light;
      }
    }

    .lbl {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .cnt {
      font-size: 12px;
      color: $gray-dark;
      font-variant-numeric: tabular-nums;
    }

    svg.chev {
      @include icon;
      width: 14px;
      height: 14px;
      flex: 0 0 auto;
      color: $gray-dark;
      transition: transform 0.16s ease;
    }

    &.open svg.chev {
      transform: rotate(90deg);
    }

    .chev-spacer {
      width: 14px;
      flex: 0 0 auto;
    }
  }

  &__sub {
    padding-left: 10px;

    .catalog-rail__item {
      font-size: 13.5px;
      color: $gray-darker;
      padding: 6px 10px;

      &.is-on {
        color: $on-primary-light;
      }
    }
  }

  &__sub2 {
    padding-left: 18px;
    border-left: 1px solid $gray-lightest;
    margin: 2px 0 4px 18px;

    .catalog-rail__item {
      font-size: 13px;
      padding: 5px 10px;
    }
  }

  &__source {
    margin: 6px 12px 0;
    padding: 11px 12px;
    border-top: 1px solid $gray-lightest;
    font-size: 11.5px;
    color: $gray-dark;
    line-height: 1.45;

    b {
      display: block;
      color: $gray-darker;
      font-weight: 700;
      margin-bottom: 2px;
    }
  }
}
</style>
